import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { getCostToCompanyReport, downloadReport, CONSOLIDATED_BASIS_NOTE, unconsolidatedNotes, type CtcRow, type CostToCompanyReport } from '../api/reports.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { Alert } from '../components/Alert.js';
import { formatMoney } from '../lib/format.js';
import type { CurrencyCode } from '@kora/types';

export const reportsCostToCompanyRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/reports/cost-to-company',
  component: ReportsCostToCompanyPage,
});

const grp = (g: string | null): string => g ?? '(none)';

function ReportsCostToCompanyPage(): JSX.Element {
  const [start, setStart] = useState('2026-01-01');
  const [end, setEnd] = useState('2026-03-31');
  const [dept, setDept] = useState('');
  const filters = { periodStart: start, periodEnd: end, ...(dept ? { department: dept } : {}) };
  const q = useQuery({
    queryKey: ['reports', 'cost-to-company', start, end, dept],
    queryFn: () => getCostToCompanyReport(filters),
    enabled: Boolean(start && end),
  });
  const [dlErr, setDlErr] = useState<unknown>(null);
  const download = async (format: 'csv' | 'pdf'): Promise<void> => {
    setDlErr(null);
    try { await downloadReport('cost-to-company', filters, format); } catch (e) { setDlErr(e); }
  };

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">{q.data?.meta.headline ?? 'Total cost to company'}</h1>
        <p className="text-sm text-gray-400">Gross employment cost plus employer statutory contributions, by department and country. Figures are per currency and never mixed.</p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-900 p-4">
        <label className="text-sm text-gray-300 space-y-1"><span>From</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} aria-label="Period start" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>To</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="Period end" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-sm text-gray-300 space-y-1"><span>Department <span className="text-gray-500">(optional)</span></span>
          <input value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Department filter" placeholder="All" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={() => void download('csv')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">CSV (opens in Excel)</button>
          <button type="button" onClick={() => void download('pdf')} disabled={!q.data} className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900 disabled:opacity-40">PDF</button>
        </div>
      </div>

      <ApiErrorNotice error={q.error} fallback="Could not load the report." />
      <ApiErrorNotice error={dlErr} fallback="Download failed." />

      {q.isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}

      {q.data ? <CtcBody report={q.data} /> : null}
    </section>
  );
}

function CtcBody({ report }: { report: CostToCompanyReport }): JSX.Element {
  const malformed = report.meta.malformedEmployerCount;
  return (
    <div className="space-y-6">
      {/* Basis note MUST render — it carries the international-remote disclosure (D7). */}
      <div className="space-y-1 text-xs text-gray-500">
        <p>{report.meta.basisNote}</p>
        {report.meta.filterNote ? <p className="text-amber">{report.meta.filterNote}</p> : null}
        {/* The malformed signal — a data-integrity readout that lives only in JSON is unseen. */}
        {malformed > 0 ? (
          <p className="text-amber">{malformed} payslip{malformed === 1 ? '' : 's'} had an unreadable employer figure and contributed 0.</p>
        ) : null}
      </div>
      {report.byCurrency.length === 0 ? (
        <Alert variant="info" compact>No cost for this period.</Alert>
      ) : (
        report.byCurrency.map((c) => (
          <div key={c.currency} className="rounded border border-gray-900 p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg text-white">{c.currency}</h2>
              <span className="text-xs text-gray-500">{c.employeeCount} employee{c.employeeCount === 1 ? '' : 's'}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Metric label="Gross" value={fmt(c.gross, c.currency)} />
              <Metric label="Employer contributions" value={fmt(c.employerContributions, c.currency)} />
              <Metric label="Cost to company" value={fmt(c.costToCompany, c.currency)} />
            </div>
            <AxisTable title="By department" currency={c.currency} rows={report.byDepartment.filter((r) => r.currency === c.currency)} />
            <AxisTable title="By country" currency={c.currency} rows={report.byCountry.filter((r) => r.currency === c.currency)} />
          </div>
        ))
      )}
      {/* FX consolidation (ADR-0031) — additive card, below the per-currency cards. */}
      {report.consolidated ? (
        <div className="rounded border border-gray-800 bg-gray-950 p-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg text-white">Consolidated ({report.consolidated.baseCurrency})</h2>
            <span className="text-xs text-gray-500">{report.consolidated.total.employeeCount} employee{report.consolidated.total.employeeCount === 1 ? '' : 's'}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Metric label="Gross" value={fmt(report.consolidated.total.gross, report.consolidated.baseCurrency)} />
            <Metric label="Employer contributions" value={fmt(report.consolidated.total.employerContributions, report.consolidated.baseCurrency)} />
            <Metric label="Cost to company" value={fmt(report.consolidated.total.costToCompany, report.consolidated.baseCurrency)} />
          </div>
          <AxisTable title="By department" currency={report.consolidated.baseCurrency} rows={report.consolidated.byDepartment} />
          <AxisTable title="By country" currency={report.consolidated.baseCurrency} rows={report.consolidated.byCountry} />
          <p className="text-xs text-gray-500">{CONSOLIDATED_BASIS_NOTE}</p>
          {unconsolidatedNotes(report.meta.unconsolidated, report.consolidated.baseCurrency).map((n) => <p key={n} className="text-xs text-amber">{n}</p>)}
        </div>
      ) : null}
    </div>
  );
}
/** Display-only money formatting; API keeps fixed-4 for export. */
function fmt(amount: string, currency: string): string {
  try { return formatMoney(amount, currency as CurrencyCode); } catch { return amount; }
}
function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return <div><div className="text-xs text-gray-500">{label}</div><div className="text-gray-200 font-mono">{value}</div></div>;
}
function AxisTable({ title, currency, rows }: { title: string; currency: string; rows: CtcRow[] }): JSX.Element {
  if (rows.length === 0) return <></>;
  return (
    <div className="space-y-1">
      <h3 className="text-xs uppercase tracking-wide text-gray-500">{title}</h3>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 text-xs">
          <th className="py-1 pr-4">Group</th><th className="py-1 pr-4 text-right">Gross</th><th className="py-1 pr-4 text-right">Employer</th><th className="py-1 pr-4 text-right">Cost to company</th><th className="py-1 text-right">#</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={grp(r.group)} className="border-t border-gray-900">
              <td className="py-1.5 pr-4 text-gray-200">{grp(r.group)}</td>
              <td className="py-1.5 pr-4 text-right font-mono text-gray-300">{fmt(r.gross, currency)}</td>
              <td className="py-1.5 pr-4 text-right font-mono text-gray-300">{fmt(r.employerContributions, currency)}</td>
              <td className="py-1.5 pr-4 text-right font-mono text-gray-300">{fmt(r.costToCompany, currency)}</td>
              <td className="py-1.5 text-right text-gray-500">{r.employeeCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
